import React from 'react';

import { ThreeDStockLifestylesCompactListItemButtonsLifestyle } from './ThreeDStockLifestylesCompactListItemButtons.types';
import { UpdateLifestyleCacheAction } from '../../../../../../lifestyles/lifestylesTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';
import { TeamNanoID } from '../../../../../../teams/teamsTypes';

import { useLifestyleFavoriteButton } from '../../../../../../lifestyles/components/buttons/LifestyleFavoriteButton/hooks/useLifestyleFavoriteButton';

import { LifestyleSearchProductsButton } from '../../../../../../lifestyles/components/buttons/LifestyleSearchProductsButton';
import { DownloadLifestyleButton } from '../../../../../../lifestyles/components/buttons/DownloadLifestyleButton';
import { DownloadLifestyleSeparatelyButton } from '../../../../../../lifestyles/components/buttons/DownloadLifestyleSeparatelyButton';
import { LifestyleAttachModalButton } from '../../../../../../lifestyles/components/buttons/LifestyleAttachModalButton';
import { LifestyleCopyLinkButton } from '../../../../../../lifestyles/components/buttons/LifestyleCopyLinkButton';

import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { DropdownHelper } from '../../../../../../../helpers/dropdowns/DropdownHelper';
import { NextLinkHelper } from '../../../../../../../helpers/links/NextLinkHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';

import { LifestylePath } from '../../../../../../lifestyles/LifestylePath';

import { LifestylesPermissions } from '../../../../../../lifestyles/lifestylesConstants';
import { PopoverPlacement } from '../../../../../../../helpers/Popover/popoverConstants';

import { aiSearchKeys, words } from '../../../../../../../locales/keys';

interface ThreeDStockLifestylesCompactListItemButtonsProps {
  lifestyle: ThreeDStockLifestylesCompactListItemButtonsLifestyle;
  updateLifestyleCache?: UpdateLifestyleCacheAction<ThreeDStockLifestylesCompactListItemButtonsLifestyle>;
  onEditButtonMouseEnter?: () => void;
  companyNanoId?: TeamNanoID;
  isMyLibrary?: boolean;
}

function ThreeDStockLifestylesCompactListItemButtons({
  lifestyle,
  updateLifestyleCache,
  onEditButtonMouseEnter,
  companyNanoId,
  isMyLibrary
}: ThreeDStockLifestylesCompactListItemButtonsProps) {
  const { handleToggleFavorite, toggleLifestyleFavoriteLoading } =
    useLifestyleFavoriteButton({
      lifestyle,
      updateIndexLifestyleCache: updateLifestyleCache
    });

  return (
    <div className="flex justify-between gap-1 z-1 min-w-0">
      <div className="rounded-full flex min-w-0 bg-blue-500">
        <CheckPermissions
          action={LifestylesPermissions.READ_LIFESTYLE_FAVORITE_BUTTON}
        >
          <ButtonHelper
            dataGa="lifestyles-lifestyle-favorite-button"
            className={
              lifestyle.favorite
                ? 'py-2.5 pl-5 pr-5 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out'
                : 'py-2.5 pl-5 pr-5 rounded-full inline-flex items-center leading-5 focus:ring-base text-white bg-blue-500 hover:bg-blue-600 shadow-sm hover:shadow-md max-w-full transition-colors duration-150 ease-out'
            }
            onClick={handleToggleFavorite}
            disabled={toggleLifestyleFavoriteLoading}
          >
            {lifestyle.favorite ? (
              <span>
                <Translate id={words.saved} />
              </span>
            ) : (
              <div className="text-sm whitespace-nowrap truncate space-x-1">
                <span>
                  <Translate id={words.save} />
                </span>
                <span className="hidden group-hover:inline group-focus-within:inline space-x-1">
                  <span className="text-blue-200 lowercase">
                    <Translate id={words.to} />
                  </span>
                  <span>
                    <Translate id={words.favorites} />
                  </span>
                </span>
              </div>
            )}
          </ButtonHelper>
        </CheckPermissions>
      </div>

      <div className="flex gap-1 xl:pr-2">
        <CheckPermissions
          action={LifestylesPermissions.READ_LIFESTYLE_DOWNLOAD_BUTTON}
        >
          <DownloadLifestyleButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="lifestyles-lifestyle-download-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            lifestyleUuid={lifestyle.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <CheckPermissions
          action={
            LifestylesPermissions.READ_LIFESTYLE_DOWNLOAD_SEPARATELY_BUTTON
          }
        >
          <DownloadLifestyleSeparatelyButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="lifestyles-lifestyle-download-separately-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            lifestyleUuid={lifestyle.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <DropdownHelper
          className="relative flex items-center justify-center"
          buttonClassName="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
          icon={IconsEnum.DOTS_HORIZONTAL_OUTLINE}
          popoverClassName="flex flex-col items-end gap-1"
          dropdownPlacement={PopoverPlacement.TOP_END}
        >
          <CheckPermissions
            action={LifestylesPermissions.READ_LIFESTYLE_SEARCH_PRODUCTS_BUTTON}
          >
            <LifestyleSearchProductsButton
              dataGa="lifestyles-lifestyle-submit-to-task-or-project-button"
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              i18nText={aiSearchKeys.findProducts}
              i18nTextClassName="inline"
              lifestyle={lifestyle}
              companyNanoId={companyNanoId}
              isMyLibrary={isMyLibrary}
            />
          </CheckPermissions>

          <CheckPermissions
            action={LifestylesPermissions.READ_LIFESTYLE_ATTACH_BUTTON}
          >
            <LifestyleAttachModalButton
              dataGa="lifestyles-lifestyle-submit-to-task-or-project-button"
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              lifestyle={lifestyle}
              i18nText={words.attach}
              i18nTextClassName="inline"
              iconClassName="w-5 h-5"
            />
          </CheckPermissions>

          <CheckPermissions
            action={LifestylesPermissions.READ_LIFESTYLE_COPY_LINK_BUTTON}
          >
            <LifestyleCopyLinkButton
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              dataGa="lifestyles-lifestyle-copy-link-button"
              icon={IconsEnum.LINK_SOLID}
              iconClassName="h-5 w-5"
              lifestyleNanoId={lifestyle.nanoId}
              i18nText={words.copyLink}
              i18nTextClassName="inline"
            />
          </CheckPermissions>

          <CheckPermissions
            action={LifestylesPermissions.READ_LIFESTYLE_EDIT_BUTTON}
          >
            <NextLinkHelper
              onMouseEnter={onEditButtonMouseEnter}
              dataGa="lifestyles-lifestyle-edit-link"
              href={LifestylePath.edit(lifestyle.nanoId)}
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            >
              <Icon icon={IconsEnum.PENCIL} className="w-5 h-5" />
              <span>
                <Translate id={words.edit} />
              </span>
            </NextLinkHelper>
          </CheckPermissions>
        </DropdownHelper>
      </div>
    </div>
  );
}

export default ThreeDStockLifestylesCompactListItemButtons;
